import type { StableViewKeyEntry } from './types';

/**
 * Maps timeline item IDs (optimistic and real) to stable view keys so React
 * can reconcile in-place when the optimistic ID is replaced by the server ID.
 * Without this, key changes cause unmount/remount and a visible "refresh".
 */
const stableViewKeys = new Map<string, StableViewKeyEntry>();
let viewKeySequence = 0;

function createStableViewKey(): string {
  viewKeySequence += 1;
  return `vk-${Date.now()}-${viewKeySequence}`;
}

/**
 * Lets different local ids intentionally share one React key inside a PR scope.
 * Review submit uses this to bridge pending ids, optimistic ids, and later real ids
 * so the same thread/comment can survive each promotion step without remounting.
 */
export function registerStableViewKey(
  itemId: string,
  scopeKey: string,
  preferredViewKey?: string,
): string {
  const existingEntry = stableViewKeys.get(itemId);
  if (existingEntry) {
    return existingEntry.viewKey;
  }

  const viewKey = preferredViewKey ?? createStableViewKey();
  stableViewKeys.set(itemId, {
    scopeKey,
    viewKey,
  });
  return viewKey;
}

/**
 * Returns a stable key for a timeline item ID, or the ID itself if no
 * stable key is registered. Used by the discussion view model to generate
 * React keys that survive the optimistic-to-real ID transition.
 */
export function getStableViewKey(itemId: string): string {
  return stableViewKeys.get(itemId)?.viewKey ?? itemId;
}

/**
 * Removes stable keys for a PR scope once neither cache references them anymore.
 * This keeps optimistic-to-real aliases bounded to the active discussion state.
 */
export function pruneStableViewKeys(scopeKey: string, activeItemIds: Iterable<string>): void {
  const activeItemIdSet = new Set<string>();
  for (const itemId of activeItemIds) {
    if (typeof itemId === 'string' && itemId.length > 0) {
      activeItemIdSet.add(itemId);
    }
  }

  for (const [itemId, entry] of stableViewKeys.entries()) {
    if (entry.scopeKey !== scopeKey) {
      continue;
    }

    if (!activeItemIdSet.has(itemId)) {
      stableViewKeys.delete(itemId);
    }
  }
}

/**
 * Preserve the same key when a local id family changes shape without requiring
 * the caller to know anything about the registry internals.
 */
export function transferStableViewKeyToId(
  fromId: string | undefined,
  toId: string | undefined | null,
): string | null {
  if (!fromId || !toId) {
    return null;
  }

  const existingEntry = stableViewKeys.get(fromId);
  if (!existingEntry) {
    return null;
  }

  registerStableViewKey(toId, existingEntry.scopeKey, existingEntry.viewKey);
  return toId;
}

/**
 * Test helper for isolating the in-memory registry between runs.
 */
export function resetStableViewKeysForTests(): void {
  stableViewKeys.clear();
  viewKeySequence = 0;
}
